import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateVisitVisitorFormValues } from './schema/create-visit-validation-schema';

import { Scrollbar } from '@/components/atoms/scrollbar';
import { Status } from '@/components/atoms/status';
import TableActionCell from '@/components/atoms/table-action-cell';
import { DiscountTemplate } from '@/features/hosts/api/types/discount';

interface Props {
  visitors: CreateVisitVisitorFormValues[];
  discountTemplates: DiscountTemplate[];
  actionsSlot?: (visitorLocalId: string) => ReactNode;
  onAddDiscount?: (visitorLocalId: string) => void;
}

const statusColor = (enabled: boolean) => (enabled ? '#1b774d' : '#a53531');

const languageFlags = {
  pl: '/static/pl_flag.svg',
  en: '/static/en_flag.svg',
};

const VisitorsTable = ({ visitors, discountTemplates, actionsSlot, onAddDiscount }: Props) => {
  const { t } = useTranslation(['createVisit', 'common']);

  const columns = [
    {
      id: 'name',
      label: t('createVisit:confirmation.visitorsTable.guest'),
    },
    {
      id: 'isHandicapped',
      label: t('createVisit:confirmation.visitorsTable.handicapped'),
    },
    {
      id: 'isPregnant',
      label: t('createVisit:confirmation.visitorsTable.pregnant'),
    },
    {
      id: 'hasPet',
      label: t('createVisit:confirmation.visitorsTable.pet'),
    },
    {
      id: 'parkingAccess',
      label: t('createVisit:confirmation.visitorsTable.parking'),
    },
    {
      id: 'discount',
      label: t('createVisit:confirmation.visitorsTable.discount'),
    },
    {
      id: 'language',
      label: t('createVisit:confirmation.visitorsTable.language'),
    },
    ...(actionsSlot
      ? [
          {
            id: 'actions',
            label: '',
          },
        ]
      : []),
  ];

  return (
    <Card variant="outlined">
      <Scrollbar>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map(({ label, id }) => (
                <TableCell key={id}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visitors.map(visitor => {
              const canAddDiscount =
                !!onAddDiscount &&
                visitor.hasAccessToParking &&
                visitor.discountTemplateId === null &&
                discountTemplates.length > 0;

              return (
                <TableRow hover key={visitor.localId}>
                  <TableCell>
                    <Typography variant="subtitle2" noWrap>
                      {visitor.firstName} {visitor.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {visitor.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Status
                      color={statusColor(visitor.isHandicapped)}
                      label={visitor.isHandicapped ? t('common:yes') : t('common:no')}
                    />
                  </TableCell>
                  <TableCell>
                    <Status
                      color={statusColor(visitor.isPregnant)}
                      label={visitor.isPregnant ? t('common:yes') : t('common:no')}
                    />
                  </TableCell>
                  <TableCell>
                    <Status
                      color={statusColor(visitor.hasPet)}
                      label={visitor.hasPet ? t('common:yes') : t('common:no')}
                    />
                  </TableCell>
                  <TableCell>
                    <Status
                      color={statusColor(visitor.hasAccessToParking)}
                      label={visitor.hasAccessToParking ? t('common:yes') : t('common:no')}
                    />
                  </TableCell>
                  <TableCell>
                    {canAddDiscount ? (
                      <Button
                        onClick={() => onAddDiscount?.(visitor.localId)}
                        size="small"
                        type="button"
                        variant="outlined">
                        {t('createVisit:visitors.addDiscount')}
                      </Button>
                    ) : (
                      (discountTemplates.find(
                        discount => discount.id === visitor.discountTemplateId,
                      )?.name ?? t('common:notAdded'))
                    )}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          height: 20,
                          width: 20,
                          '& img': {
                            width: '100%',
                          },
                        }}>
                        <img alt={visitor.language} src={languageFlags[visitor.language]} />
                      </Box>
                    </Box>
                  </TableCell>
                  {actionsSlot && <TableActionCell>{actionsSlot(visitor.localId)}</TableActionCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Scrollbar>
    </Card>
  );
};

export default VisitorsTable;
